import React, { useEffect, useState } from 'react';
import { auditService, AuditLogEntry } from '../services/auditService';
import { useDateTime } from '../hooks/useDateTime';
import './AuditLog.css';

const ACTION_LABELS: Record<string, string> = {
  PASSWORD_RESET: 'Password reset',
  RESEND_VERIFICATION: 'Resend verification',
  TWO_FACTOR_ENABLED: '2FA enabled',
  TWO_FACTOR_DISABLED: '2FA disabled',
  TWO_FACTOR_RECOVERY_REGENERATED: 'Recovery codes regenerated',
  USER_DELETED: 'User deleted',
  COMPANY_DELETED: 'Company deleted',
  TICKETS_EXPORTED: 'Tickets exported',
  TICKETS_IMPORTED: 'Tickets imported',
  ACCOUNT_LOCKED: 'Account locked',
  PROJECTS_TRIAL_STARTED: 'Projects demo started',
  PROJECTS_EXPORTED: 'Projects exported',
  PROJECTS_IMPORTED: 'Projects imported',
  PROJECTS_DELETED: 'Projects deleted'
};

const AuditLog: React.FC = () => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const { formatDateTime } = useDateTime();

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await auditService.getLogs({ page, limit: 50, action, search });
      setLogs(response.data);
      setPages(response.pagination.pages || 1);
      setTotal(response.pagination.total || 0);
    } catch (error) {
      console.error('Error loading audit logs', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, action]);

  const formatDate = (value: string) => formatDateTime(value);

  const actorName = (log: AuditLogEntry) => {
    if (log.actor) {
      return `${log.actor.firstName} ${log.actor.lastName}`;
    }
    return log.actorEmail || 'System';
  };

  return (
    <div className="audit-log">
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit log</h1>
          <p className="page-subtitle">Critical actions: password reset, 2FA, resend verification, account and company deletion</p>
        </div>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label>Action</label>
          <select value={action} onChange={(e) => { setPage(1); setAction(e.target.value); }}>
            <option value="">All actions</option>
            {Object.entries(ACTION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <label>Search</label>
          <input
            type="text"
            value={search}
            placeholder="Email or resource"
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                loadLogs();
              }
            }}
          />
        </div>
      </div>

      <div className="users-table">
        {loading ? (
          <div className="spinner-wrapper">
            <div className="spinner">Loading...</div>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Time</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Resource</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log._id}>
                  <td>{formatDate(log.createdAt)}</td>
                  <td>
                    <div>{actorName(log)}</div>
                    <small>{log.actorEmail || log.actor?.email || '-'}</small>
                  </td>
                  <td>{ACTION_LABELS[log.action] || log.action}</td>
                  <td>{log.resourceLabel || log.resourceId || log.resourceType}</td>
                  <td>{log.ip || '-'}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={5}>No audit records.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="audit-pagination">
        <span>{total} records</span>
        <button type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
        <span>Page {page} / {pages}</span>
        <button type="button" disabled={page >= pages} onClick={() => setPage((current) => current + 1)}>Next</button>
      </div>
    </div>
  );
};

export default AuditLog;
